import { useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'

import { Purpose } from '@utils/aureus'
import useHasAureusJsClientScriptInitialized from '@hooks/useHasAureusJsClientScriptInitialized'
import usePageMetadata from '@hooks/usePageMetadata'
import { getRecommendations as getArticleRecommendations } from '@widgets/Recommendations/utils'
import { JSONWidgetType as TeaserStandardHorizontalJSONWidgetType } from '@widgets/TeaserStandardHorizontal'

export type RecommendedItem = TeaserStandardHorizontalJSONWidgetType & {
  aureusOfferId?: string
}

export interface RecommendationsQueryData {
  data: RecommendedItem[]
}

export type NormalizedRecommendationsQueryData =
  Required<RecommendationsQueryData> & { isPlaceholderData?: boolean }

export type UseRecommendations = (arg?: {
  defaultData?: RecommendedItem[]
  forceDisable?: boolean
}) => NormalizedRecommendationsQueryData

const getRecommendations = async (
  id: string,
  purpose?: Purpose,
): Promise<RecommendedItem[]> => getArticleRecommendations(id, purpose)

const useRecommendations: UseRecommendations = ({
  defaultData,
  forceDisable,
} = {}) => {
  const { id: articleId } = usePageMetadata()

  const hasAureusJsClientScriptInitialized =
    useHasAureusJsClientScriptInitialized()

  const placeholderData = useMemo<NormalizedRecommendationsQueryData>(
    () => ({
      data: defaultData ?? [],
    }),
    [defaultData],
  )

  const { data: queryData, isPlaceholderData } =
    useQuery<RecommendationsQueryData>({
      queryKey: ['recommendations', articleId] as const,
      placeholderData,
      queryFn: async () => ({
        data: await getRecommendations(articleId as string),
      }),
      enabled:
        !forceDisable && !!articleId && hasAureusJsClientScriptInitialized,
    })

  return useMemo<NormalizedRecommendationsQueryData>(
    () => ({
      ...(isPlaceholderData || !queryData?.data
        ? placeholderData
        : (queryData as NormalizedRecommendationsQueryData)),
      isPlaceholderData,
    }),
    [isPlaceholderData, queryData, placeholderData],
  )
}

export { useRecommendations }
